import { Fragment } from 'react';
import { Link } from '@tanstack/react-router';
import { useT } from '../i18n/index.js';
import type { Pestana } from '../lib/secciones.js';

/**
 * Four tabs with this many letters between them do not fit a phone at the strip's size, and every
 * one lost its last letters: «Contabilidad | Efectivo | Gastos | Planilla» under 390 px, and
 * «Productos | Precios/Costos | Fotos | Paquetes» (2026-10-21) at 390 too.
 */
const LETRAS_DE_MAS = 28;

/** «Precios/Costos» may go onto two lines after its slash, and nowhere else. */
const conCorte = (nombre: string) =>
  nombre.split('/').map((parte, i, partes) => (
    <Fragment key={i}>
      {parte}
      {i < partes.length - 1 && (
        <>
          /<wbr />
        </>
      )}
    </Fragment>
  ));

/**
 * Las pestañas de una sección: the screens next door, one tap away.
 *
 * Above the title rather than under it. Under it, the strip sat wherever each screen's header
 * happened to end, lower on a screen whose buttons wrap, and a row of tabs that jumps as you move
 * along it reads as a different row each time. Up here it is in the same place on every tab, and
 * moving between them is the screen changing under a strip that stays put — which is also what
 * the transition shows (`.vt-pestanas` in styles.css).
 *
 * Each tab replaces the current entry in the history instead of adding one, so the phone's back
 * button, and «Volver», which takes the same step (`lib/volver.ts`), leave the section rather than
 * stepping back through the tabs one at a time.
 */
export function Pestanas({ pestanas, actual }: { pestanas: readonly Pestana[]; actual: string }) {
  const t = useT();
  const nombres = pestanas.map((p) => t(p.nombre));
  /**
   * Those, on a phone, in smaller letters, a slash's two words one over the other, and each tab no
   * narrower than its longest word, so every word is whole however the strip shares its width.
   */
  const apretada = pestanas.length >= 4 && nombres.join('').length > LETRAS_DE_MAS;
  /**
   * Five («Productos | Precios/Costos | Fotos | Paquetes | Ofertas», 2026-11-03) went a few pixels past a
   * 320 px phone even so: under 420 px they sit closer, a tab's sides and the gaps between them halved.
   */
  const cinco = apretada && pestanas.length >= 5;
  return (
    <nav
      aria-label={t('seccion.pestanas')}
      className={`vt-pestanas mb-5 flex gap-1 rounded-2xl bg-slate-900 p-1 ring-1 ring-white/5 ring-inset ${cinco ? 'max-[419px]:gap-0.5' : ''}`}
      data-guia="pestanas"
    >
      {pestanas.map((p, i) => {
        const activa = p.ruta === actual;
        return (
          <Link
            key={p.ruta}
            to={p.ruta}
            replace
            aria-current={activa ? 'page' : undefined}
            data-sin-icono="pestana"
            // Four tabs across a 320 px phone (the owner's counter: Ventas, Caja, Recargas, Control)
            // needed a fraction of a pixel more than there was, and every one of them lost its
            // last letters to an ellipsis. Narrower padding there, the words whole.
            className={`flex min-h-11 flex-auto items-center justify-center rounded-xl px-2 text-sm transition ${
              apretada ? `max-[419px]:text-xs ${cinco ? 'max-[419px]:px-0.5' : 'max-[419px]:px-1'}` : 'min-w-0 max-[359px]:px-1.5'
            } ${
              activa
                ? 'bg-slate-700/80 font-semibold text-white shadow-sm shadow-black/30 ring-1 ring-white/10 ring-inset'
                : 'font-medium text-slate-400 hover:bg-white/5 hover:text-slate-200'
            }`}
          >
            {apretada ? <span className="text-center leading-tight">{conCorte(nombres[i]!)}</span> : <span className="truncate">{nombres[i]}</span>}
          </Link>
        );
      })}
    </nav>
  );
}
